import { z } from "zod";

// Legal-documents CMS contracts (issue #305, ADR-0019 rev. 2026-07-12). Two
// versioned, admin-editable markdown documents — the **Regulamin** and the
// **Polityka prywatności** (see CONTEXT.md) — rendered on public pages. The
// mechanism is GENERIC over `slug`: the table, repo, and API endpoints branch on
// nothing here; the known set below only drives the admin section index + the
// placeholder seed + the two public route files. Adding a third document later is
// a new slug in this list (+ a thin route file), not a change to the mechanism.
//
// NOTE (#305 vs #308): this slice ships the empty CMS + wiring only. Real legal
// content is authored under a separate HITL issue (#308); the seed lands empty
// placeholder DRAFTS, never real legal text.
export const LEGAL_DOCUMENT_SLUGS = ["regulamin", "polityka-prywatnosci"] as const;
export type LegalDocumentSlug = (typeof LEGAL_DOCUMENT_SLUGS)[number];
export const LegalDocumentSlugSchema = z.enum(LEGAL_DOCUMENT_SLUGS);

// PL display titles for the known documents (the admin section index + the public
// page heading). The public frontend still routes these through i18next; this is
// the operator-console + seed source of truth. Named to match CONTEXT.md exactly.
export const LEGAL_DOCUMENT_TITLE_PL: Record<LegalDocumentSlug, string> = {
  regulamin: "Regulamin",
  "polityka-prywatnosci": "Polityka prywatności",
};

// Upper bound on stored markdown so a runaway paste can't bloat a row. Legal
// documents are long-form, so this is generous (~200k chars) — a guard, not a
// product limit. Enforced on both the draft-save and the publish request.
export const LEGAL_DOCUMENT_CONTENT_MAX = 200_000;

// Public read (`GET /legal-documents/:slug`) — the latest PUBLISHED version of a
// document. `version` is the monotonic per-slug version (>= 1); `contentMd` is the
// raw markdown the frontend renders (react-markdown + rehype-sanitize);
// `publishedAt` is an ISO-8601 string. A slug with no published version 404s — a
// still-unpublished draft is never served here.
export const PublicLegalDocument = z.object({
  slug: LegalDocumentSlugSchema,
  version: z.number().int().positive(),
  contentMd: z.string(),
  publishedAt: z.string(),
});
export type PublicLegalDocument = z.infer<typeof PublicLegalDocument>;

// One row of the admin "Dokumenty prawne" section index (`GET /admin/legal-documents`).
// A summary of each known document's state — enough to render the list + a
// "szkic" / "opublikowano wersję N" badge without opening the editor. `hasDraft` is
// true when an unpublished draft exists (i.e. there are pending changes);
// `latestVersion` / `publishedAt` are the last published version's number + time
// (null when never published).
export const AdminLegalDocumentSummary = z.object({
  slug: LegalDocumentSlugSchema,
  title: z.string(),
  latestVersion: z.number().int().positive().nullable(),
  publishedAt: z.string().nullable(),
  hasDraft: z.boolean(),
});
export type AdminLegalDocumentSummary = z.infer<typeof AdminLegalDocumentSummary>;

export const AdminLegalDocumentsResponse = z.object({
  documents: z.array(AdminLegalDocumentSummary),
});
export type AdminLegalDocumentsResponse = z.infer<typeof AdminLegalDocumentsResponse>;

// Admin editor state (`GET /admin/legal-documents/:slug`) — the summary plus the
// current editable BUFFER: the unpublished draft if one exists, else the latest
// published content, else empty.
export const AdminLegalDocument = AdminLegalDocumentSummary.extend({
  contentMd: z.string(),
});
export type AdminLegalDocument = z.infer<typeof AdminLegalDocument>;

// Body of `PUT /admin/legal-documents/:slug/draft` — save the working copy. Empty
// is allowed (a draft in progress); only the length cap applies.
export const AdminLegalDocumentSaveDraftRequest = z.object({
  contentMd: z.string().max(LEGAL_DOCUMENT_CONTENT_MAX),
});
export type AdminLegalDocumentSaveDraftRequest = z.infer<typeof AdminLegalDocumentSaveDraftRequest>;

// Body of `POST /admin/legal-documents/:slug/publish` — mint a new immutable
// version from the given markdown. Rejected when blank (publishing an empty or
// whitespace-only document is meaningless) + length-capped. The content is stored
// VERBATIM (not trimmed) so the admin editor buffer round-trips exactly and its
// dirty state settles after a publish. The server assigns the version + `published_at`.
export const AdminLegalDocumentPublishRequest = z.object({
  contentMd: z
    .string()
    .min(1)
    .max(LEGAL_DOCUMENT_CONTENT_MAX)
    .refine((s) => s.trim().length > 0, { message: "Content must not be blank." }),
});
export type AdminLegalDocumentPublishRequest = z.infer<typeof AdminLegalDocumentPublishRequest>;
